import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Monitor, Sparkles, ArrowRight, Loader2, Check, Mail } from "lucide-react";
import { joinWaitlist, waitlistCount } from "../lib/api";
import { track } from "../lib/track";
import { toast } from "sonner";

/** Pulls live count, baseline +47 server-side. */
function useWaitlistCount() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    waitlistCount()
      .then((d) => setCount(d.count))
      .catch(() => setCount(47));
  }, []);
  return [count, setCount];
}

/**
 * Compact teaser for the landing page. Single line + CTA.
 */
export function WaitlistTeaser() {
  const [open, setOpen] = useState(false);
  const [count] = useWaitlistCount();
  return (
    <>
      <button
        data-testid="waitlist-teaser"
        onClick={() => setOpen(true)}
        className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#e5e7eb] text-[#1f2937] text-[12px] hover:border-indigo-300 hover:bg-[#f7f8fa] transition"
      >
        <Monitor size={12} strokeWidth={2.2} className="text-indigo-500" />
        <span>
          <strong className="font-semibold">Trace Desktop</strong> is coming. Local. Real-time.
        </span>
        <ArrowRight size={12} className="text-indigo-500 transition-transform duration-300 group-hover:translate-x-0.5" />
      </button>
      <WaitlistModal
        open={open}
        onOpenChange={setOpen}
        count={count}
        repoFullName={null}
      />
    </>
  );
}

/**
 * Bigger block for the Resume page (placed below the main card).
 */
export function WaitlistBlock({ repoFullName }) {
  const [open, setOpen] = useState(false);
  const [count] = useWaitlistCount();
  return (
    <>
      <div
        data-testid="waitlist-block"
        className="mt-10 relative rounded-2xl border border-[#e5e7eb] bg-white p-6 sm:p-7 overflow-hidden"
      >
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-30 bg-gradient-to-br from-indigo-400 to-blue-400 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 max-w-xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-indigo-500 shadow-sm">
                <Monitor size={12} className="text-white" strokeWidth={2.4} />
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-indigo-700 font-bold">
                Trace Desktop · Coming soon
              </span>
            </div>
            <h3 className="text-[20px] sm:text-[22px] tracking-tight text-[#0b1220] font-semibold leading-snug">
              This is just the beginning.
            </h3>
            <p className="mt-2 text-[13.5px] text-[#1f2937] leading-relaxed">
              Trace Desktop runs locally and tracks your <em>actual</em> work — not just commits —
              so you never lose context again.
            </p>
            <ul className="mt-3 space-y-1.5 text-[13px] text-[#1f2937]">
              <li className="flex items-start gap-2">
                <Check size={13} strokeWidth={2.4} className="text-emerald-500 mt-1 shrink-0" />
                Knows the file you were editing
              </li>
              <li className="flex items-start gap-2">
                <Check size={13} strokeWidth={2.4} className="text-emerald-500 mt-1 shrink-0" />
                Works even without GitHub
              </li>
              <li className="flex items-start gap-2">
                <Check size={13} strokeWidth={2.4} className="text-emerald-500 mt-1 shrink-0" />
                Instant resume the moment you reopen your project
              </li>
            </ul>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
            <button
              data-testid="waitlist-block-cta"
              onClick={() => setOpen(true)}
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(99,102,241,0.4)] transition-all duration-300"
            >
              Get early access
              <ArrowRight
                size={14}
                strokeWidth={2.4}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </button>
            {count !== null && (
              <span className="text-[11px] text-[#4b5563]">
                <span className="font-semibold text-[#1f2937]">{count}</span> developers waiting
              </span>
            )}
          </div>
        </div>
      </div>
      <WaitlistModal
        open={open}
        onOpenChange={setOpen}
        count={count}
        repoFullName={repoFullName}
      />
    </>
  );
}

/** Modal — shared. */
export function WaitlistModal({ open, onOpenChange, count, repoFullName }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => {
    if (!open) {
      setEmail("");
      setDone(false);
      setSubmitting(false);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await joinWaitlist(email.trim(), repoFullName || null);
      track("Waitlist Joined", { props: { source: repoFullName ? "project" : "landing" } });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border border-[#e5e7eb] sm:max-w-[440px] p-6">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-indigo-500 shadow-sm">
              <Sparkles size={13} className="text-white" strokeWidth={2.4} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-indigo-700 font-bold">
              Trace Desktop
            </span>
          </div>
          <DialogTitle className="text-[22px] tracking-tight text-[#0b1220] mt-3">
            {done ? "You're in." : "Get early access to Trace Desktop"}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#4b5563] leading-relaxed">
            {done
              ? "We'll let you know first. Want to help shape it? Reply to our launch post on X."
              : "Be among the first to use the local version that remembers your actual work — in real time."}
          </DialogDescription>
        </DialogHeader>

        {!done ? (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <div className="relative">
              <Mail
                size={14}
                strokeWidth={2.2}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]"
              />
              <input
                data-testid="waitlist-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dev.com"
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#f7f8fa] border border-[#e5e7eb] outline-none focus:border-indigo-400 transition text-sm text-[#111827]"
              />
            </div>
            <button
              data-testid="waitlist-submit"
              type="submit"
              disabled={submitting || !valid}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(99,102,241,0.4)] transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArrowRight size={14} strokeWidth={2.4} />
              )}
              Notify me
            </button>
            <div className="flex items-center justify-between text-[11px] text-[#4b5563]">
              <span>No spam. Just when it's ready.</span>
              {count !== null && (
                <span>
                  <span className="font-semibold text-[#1f2937]">{count}</span> waiting
                </span>
              )}
            </div>
          </form>
        ) : (
          <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <Check size={16} strokeWidth={2.6} className="text-emerald-600 mt-0.5 shrink-0" />
            <div className="text-[13px] text-emerald-900 leading-relaxed">
              You're #{count ?? "—"} in line. We'll email <strong>{email}</strong> the moment Trace
              Desktop is ready.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
